import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Copy,
  GitBranch,
  Loader2,
  Plus,
  RefreshCw,
  Unlink,
  X,
} from 'lucide-react';

import {
  knowledgeService,
  type SubjectValueBindingOptions,
} from '../services/knowledgeService';
import type { KnowledgeParticle } from '../types/domain';
import {
  buildSubjectValueBindingView,
  getSubjectValueBindingRecord,
  isKnowledgeParticleBackfillQueueResult,
  resolveEnrichmentBackfillDegradedMessage,
} from '../utils/subjectValueBinding';

type SubjectSpineBindingChipProps = {
  particleId: string;
  metadata?: Record<string, unknown> | null;
  onParticleUpdated?: (particle: KnowledgeParticle) => void;
  onToast?: (message: string) => void;
  className?: string;
};

type EditTarget = 'primary' | 'secondary';

type BindingAnchorFeedback = {
  subjectValueProfileId: string;
  spineId: string;
  spineNodeId: string;
  spineNodeTitle?: string;
  profileDisplayName?: string;
} | null;

type BindingFeedback = {
  action: 'bind_primary' | 'bind_secondary' | 'clear_primary' | 'clear_secondary' | 'rebuild';
  at: string;
  particleId: string;
  primary: BindingAnchorFeedback;
  secondary: BindingAnchorFeedback;
  bindingStatus: string | null;
};

function readText(record: Record<string, unknown> | null | undefined, keys: string[]) {
  if (!record) return '';
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

function formatAnchorLabel(anchor: Record<string, unknown> | undefined) {
  if (!anchor) return '';
  const moduleLabel = readText(anchor, ['moduleLabel', 'moduleKey']);
  const spineNodeTitle = readText(anchor, ['spineNodeTitle', 'spineNodeId']);
  return [moduleLabel, spineNodeTitle].filter(Boolean).join(' / ');
}

function toneClasses(tone: 'default' | 'success' | 'warning') {
  if (tone === 'success') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100';
  }
  if (tone === 'warning') {
    return 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100';
  }
  return 'border-border bg-canvas text-muted hover:bg-black/[0.04] hover:text-ink';
}

function isKnowledgeParticle(value: unknown): value is KnowledgeParticle {
  return Boolean(value && typeof value === 'object' && typeof (value as { id?: unknown }).id === 'string');
}

export function SubjectSpineBindingChip({
  particleId,
  metadata,
  onParticleUpdated,
  onToast,
  className = '',
}: SubjectSpineBindingChipProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTarget>('primary');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [profileId, setProfileId] = useState('');
  const [nodeKey, setNodeKey] = useState('');
  const [options, setOptions] = useState<SubjectValueBindingOptions | null>(null);
  const [feedback, setFeedback] = useState<BindingFeedback | null>(null);
  const [copied, setCopied] = useState(false);

  const binding = useMemo(() => getSubjectValueBindingRecord(metadata || undefined), [metadata]);
  const secondary = useMemo(() => asRecord(binding?.secondary), [binding]);
  const primaryBound = bindingViewStatusBound(binding);
  const secondaryLabel = formatAnchorLabel(secondary);

  const bindingView = useMemo(
    () => buildSubjectValueBindingView(metadata || undefined),
    [metadata],
  );

  const summary = useMemo(() => {
    const moduleLabel = bindingView.rows.find((item) => item.label === '模块')?.value;
    const spineNodeTitle = bindingView.rows.find((item) => item.label === '主干节点')?.value;
    if (bindingView.status === 'bound') {
      return [moduleLabel, spineNodeTitle].filter(Boolean).join(' / ') || bindingView.shortText;
    }
    return bindingView.shortText;
  }, [bindingView]);

  const chipLabel = useMemo(() => {
    if (bindingView.status === 'bound' && summary) {
      return summary;
    }
    return `学科主干 · ${bindingView.badgeLabel}`;
  }, [bindingView.badgeLabel, bindingView.status, summary]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  const openEditor = (target: EditTarget) => {
    if (target === 'secondary' && !primaryBound) {
      setError('请先绑定主学科主干，再追加次级');
      return;
    }

    setEditTarget(target);
    setMenuOpen(false);
    setEditorOpen(true);
    setLoadingOptions(true);
    setError(null);
    setQuery('');
    setNodeKey('');
    setOptions(null);

    void knowledgeService.listSubjectValueBindingOptions(particleId)
      .then((next) => {
        setOptions(next);
        const current = next.current;
        const secondaryCurrent = asRecord(current?.secondary);
        const source = target === 'secondary' ? secondaryCurrent : current;
        const currentProfileId = readText(source || undefined, ['subjectValueProfileId'])
          || readText(current || undefined, ['subjectValueProfileId']);
        const preferred = next.profiles.find((profile) => profile.profileId === currentProfileId)
          || next.profiles[0];
        setProfileId(preferred?.profileId || '');
        const spineId = readText(source || undefined, ['spineId']);
        const nodeId = readText(source || undefined, ['spineNodeId']);
        if (preferred && spineId && nodeId) {
          const matched = preferred.nodes.find((node) => node.spineId === spineId && node.nodeId === nodeId);
          setNodeKey(matched ? `${preferred.profileId}::${matched.spineId}::${matched.nodeId}` : '');
        }
      })
      .catch((loadError) => {
        setError(loadError instanceof Error ? loadError.message : '加载学科主干选项失败');
      })
      .finally(() => setLoadingOptions(false));
  };

  const selectedProfile = options?.profiles.find((item) => item.profileId === profileId);
  const filteredNodes = useMemo(() => {
    const nodes = selectedProfile?.nodes || [];
    const needle = query.trim().toLowerCase();
    if (!needle) return nodes;
    return nodes.filter((node) => {
      const haystack = [
        node.nodeTitle,
        node.spineTitle,
        node.moduleLabel,
        node.moduleKey,
        node.nodeId,
        node.spineId,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [query, selectedProfile]);

  const publishFeedback = (next: BindingFeedback) => {
    setFeedback(next);
    setCopied(false);
  };

  const buildFeedbackFromParticle = (
    updated: KnowledgeParticle,
    action: BindingFeedback['action'],
  ): BindingFeedback => {
    const nextBinding = getSubjectValueBindingRecord(
      updated.metadata && typeof updated.metadata === 'object'
        ? updated.metadata as Record<string, unknown>
        : undefined,
    );
    const nextSecondary = asRecord(nextBinding?.secondary);
    return {
      action,
      at: new Date().toISOString(),
      particleId,
      primary: nextBinding && readText(nextBinding, ['spineNodeId'])
        ? {
            subjectValueProfileId: readText(nextBinding, ['subjectValueProfileId']),
            spineId: readText(nextBinding, ['spineId']),
            spineNodeId: readText(nextBinding, ['spineNodeId']),
            spineNodeTitle: readText(nextBinding, ['spineNodeTitle']),
            profileDisplayName: readText(nextBinding, ['profileDisplayName']),
          }
        : null,
      secondary: nextSecondary && readText(nextSecondary, ['spineNodeId'])
        ? {
            subjectValueProfileId: readText(nextSecondary, ['subjectValueProfileId']),
            spineId: readText(nextSecondary, ['spineId']),
            spineNodeId: readText(nextSecondary, ['spineNodeId']),
            spineNodeTitle: readText(nextSecondary, ['spineNodeTitle']),
            profileDisplayName: readText(nextSecondary, ['profileDisplayName']),
          }
        : null,
      bindingStatus: readText(nextBinding, ['status']) || null,
    };
  };

  const handleSave = async () => {
    const profile = options?.profiles.find((item) => item.profileId === profileId);
    const node = profile?.nodes.find(
      (item) => `${profile.profileId}::${item.spineId}::${item.nodeId}` === nodeKey,
    );
    if (!profile || !node) {
      setError('请选择画像与主干节点');
      return;
    }

    if (
      editTarget === 'secondary'
      && binding
      && readText(binding, ['spineNodeId']) === node.nodeId
      && readText(binding, ['spineId']) === node.spineId
    ) {
      setError('次级学科不能与主锚选择同一主干节点');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const anchor = {
        subjectValueProfileId: profile.profileId,
        spineId: node.spineId,
        spineNodeId: node.nodeId,
        reason: editTarget === 'secondary'
          ? 'WebApp2 适配屏幕手动追加次级学科主干'
          : 'WebApp2 适配屏幕手动绑定学科主干',
      };
      const updated = await knowledgeService.putSubjectValueBinding(
        particleId,
        editTarget === 'secondary' ? { secondary: anchor } : { primary: anchor },
      );
      onParticleUpdated?.(updated);
      publishFeedback(buildFeedbackFromParticle(
        updated,
        editTarget === 'secondary' ? 'bind_secondary' : 'bind_primary',
      ));
      setEditorOpen(false);
      onToast?.(
        editTarget === 'secondary'
          ? `已追加次级：${node.nodeTitle || node.nodeId}`
          : `已绑定：${node.nodeTitle || node.nodeId}`,
      );
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '绑定失败');
    } finally {
      setSaving(false);
    }
  };

  const handleClearPrimary = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await knowledgeService.putSubjectValueBinding(particleId, { primary: null });
      onParticleUpdated?.(updated);
      publishFeedback(buildFeedbackFromParticle(updated, 'clear_primary'));
      setMenuOpen(false);
      setEditorOpen(false);
      onToast?.('已取消学科主干绑定');
    } catch (clearError) {
      setError(clearError instanceof Error ? clearError.message : '取消绑定失败');
    } finally {
      setSaving(false);
    }
  };

  const handleClearSecondary = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await knowledgeService.putSubjectValueBinding(particleId, { secondary: null });
      onParticleUpdated?.(updated);
      publishFeedback(buildFeedbackFromParticle(updated, 'clear_secondary'));
      setMenuOpen(false);
      setEditorOpen(false);
      onToast?.('已清除次级学科绑定');
    } catch (clearError) {
      setError(clearError instanceof Error ? clearError.message : '清除次级失败');
    } finally {
      setSaving(false);
    }
  };

  const handleRebuild = async () => {
    if (rebuilding) return;
    setRebuilding(true);
    setError(null);
    setMenuOpen(false);
    onToast?.('已触发重新绑定，正在补齐…');

    try {
      const result = await knowledgeService.backfillKnowledgeParticleBinding(particleId, {
        force: true,
        wait: false,
      });

      if (isKnowledgeParticle(result)) {
        onParticleUpdated?.(result);
        const degraded = resolveEnrichmentBackfillDegradedMessage(
          result.metadata && typeof result.metadata === 'object'
            ? result.metadata as Record<string, unknown>
            : undefined,
        );
        if (degraded) {
          setError(degraded);
          onToast?.(degraded);
          return;
        }
        publishFeedback(buildFeedbackFromParticle(result, 'rebuild'));
        onToast?.('重新绑定已完成');
        return;
      }

      if (isKnowledgeParticleBackfillQueueResult(result) && result.status === 'skipped') {
        setError('当前无法重新绑定（任务被跳过）');
        onToast?.('重新绑定被跳过');
        return;
      }

      // 异步排队：轮询几次拿最新颗粒
      for (let attempt = 0; attempt < 8; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 1200 : 2000));
        const nextParticle = await knowledgeService.getKnowledgeParticle(particleId);
        onParticleUpdated?.(nextParticle);
        const enrichment = asRecord(
          asRecord(nextParticle.metadata)?.revisionFamilyEnrichment,
        );
        const status = readText(enrichment, ['status']).toLowerCase();
        if (status && status !== 'processing' && status !== 'queued') {
          const degraded = resolveEnrichmentBackfillDegradedMessage(
            nextParticle.metadata && typeof nextParticle.metadata === 'object'
              ? nextParticle.metadata as Record<string, unknown>
              : undefined,
          );
          if (degraded) {
            setError(degraded);
            onToast?.(degraded);
          } else {
            publishFeedback(buildFeedbackFromParticle(nextParticle, 'rebuild'));
            onToast?.('重新绑定已完成');
          }
          return;
        }
      }
      onToast?.('重新绑定仍在后台处理，稍后刷新查看');
    } catch (rebuildError) {
      const message = rebuildError instanceof Error ? rebuildError.message : '重新绑定失败';
      setError(message);
      onToast?.(message);
    } finally {
      setRebuilding(false);
    }
  };

  const handleCopyFeedback = async () => {
    if (!feedback) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(feedback, null, 2));
      setCopied(true);
      onToast?.('绑定反馈已复制，可粘贴给我');
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      onToast?.('复制失败，请手动选中下方文本');
    }
  };

  const busy = saving || rebuilding;
  const itemClass = 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-50';
  const dangerClass = 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/5 disabled:opacity-50';

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        className={`inline-flex max-w-[min(100%,18rem)] items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition ${toneClasses(bindingView.badgeTone)}`}
        title={secondaryLabel ? `${summary} · 次级 ${secondaryLabel}` : summary}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={(event) => {
          event.stopPropagation();
          setMenuOpen((open) => !open);
          setError(null);
        }}
      >
        <GitBranch size={11} className="shrink-0" />
        <span className="truncate">{chipLabel}</span>
      </button>

      {menuOpen ? (
        <div
          className="absolute bottom-[calc(100%+6px)] right-0 z-40 w-[17.5rem] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg"
          role="menu"
        >
          <div className="border-b border-border/70 px-3 py-2">
            <p className="text-[11px] font-semibold text-ink">学科主干</p>
            <p className="mt-0.5 line-clamp-2 text-[11px] text-muted">主锚：{summary}</p>
            {secondaryLabel ? (
              <p className="mt-0.5 line-clamp-2 text-[11px] text-muted">次级：{secondaryLabel}</p>
            ) : null}
          </div>

          <button
            type="button"
            className={itemClass}
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              openEditor('primary');
            }}
          >
            <GitBranch size={14} />
            {primaryBound ? '手动改绑主锚…' : '手动绑定…'}
          </button>

          <button
            type="button"
            className={itemClass}
            disabled={busy || !primaryBound}
            title={primaryBound ? undefined : '需先绑定主锚'}
            onClick={(event) => {
              event.stopPropagation();
              openEditor('secondary');
            }}
          >
            <Plus size={14} />
            {secondaryLabel ? '改绑次级学科…' : '追加次级学科…'}
          </button>

          <button
            type="button"
            className={itemClass}
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              void handleRebuild();
            }}
          >
            {rebuilding ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {rebuilding ? '重新绑定中…' : '重新绑定（重构补充）'}
          </button>

          {secondaryLabel ? (
            <button
              type="button"
              className={dangerClass}
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                void handleClearSecondary();
              }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Unlink size={14} />}
              清除次级绑定
            </button>
          ) : null}

          {primaryBound ? (
            <button
              type="button"
              className={dangerClass}
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                void handleClearPrimary();
              }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Unlink size={14} />}
              取消绑定
            </button>
          ) : null}

          {error && !editorOpen ? (
            <p className="px-3 py-2 text-[11px] text-danger">{error}</p>
          ) : null}
        </div>
      ) : null}

      {feedback ? (
        <div className="absolute top-[calc(100%+6px)] right-0 z-40 w-[18rem] rounded-lg border border-border bg-surface p-2 shadow-lg">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-[11px] font-semibold text-ink">绑定反馈（可粘贴）</p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-primary hover:bg-primary/5"
                onClick={(event) => {
                  event.stopPropagation();
                  void handleCopyFeedback();
                }}
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? '已复制' : '复制'}
              </button>
              <button
                type="button"
                className="rounded p-0.5 text-muted hover:bg-black/[0.04] hover:text-ink"
                aria-label="关闭反馈"
                onClick={(event) => {
                  event.stopPropagation();
                  setFeedback(null);
                }}
              >
                <X size={12} />
              </button>
            </div>
          </div>
          <pre className="max-h-36 overflow-auto whitespace-pre-wrap break-all rounded bg-canvas px-2 py-1.5 text-[10px] leading-relaxed text-ink">
            {JSON.stringify(feedback, null, 2)}
          </pre>
        </div>
      ) : null}

      {editorOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
          onClick={() => {
            if (!saving) setEditorOpen(false);
          }}
        >
          <div
            className="flex max-h-[min(88vh,36rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-ink">
                  {editTarget === 'secondary' ? '追加次级学科主干' : '手动绑定学科主干'}
                </h3>
                <p className="mt-0.5 text-xs text-muted">
                  {editTarget === 'secondary'
                    ? '在已有主锚基础上追加辅锚；学情图当前仍以主锚为准。'
                    : '选择画像模块下的主干节点，保存为手动绑定。'}
                </p>
              </div>
              <button
                type="button"
                className="rounded-md p-1 text-muted hover:bg-black/[0.04] hover:text-ink"
                disabled={saving}
                onClick={() => setEditorOpen(false)}
                aria-label="关闭"
              >
                <X size={16} />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
              {loadingOptions ? (
                <div className="flex items-center gap-2 py-8 text-sm text-muted">
                  <Loader2 size={16} className="animate-spin" />
                  加载可选主干…
                </div>
              ) : null}

              {!loadingOptions && options ? (
                <>
                  {options.runtimeStatus !== 'ready' ? (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      {options.runtimeMessage || '学科画像暂不可用，可选节点可能为空。'}
                    </p>
                  ) : null}

                  <div>
                    <label className="text-xs font-semibold text-muted">画像</label>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {options.profiles.map((profile) => (
                        <button
                          key={profile.profileId}
                          type="button"
                          className={`rounded-full border px-2.5 py-1 text-xs ${
                            profileId === profile.profileId
                              ? 'border-primary bg-primary/10 font-semibold text-primary'
                              : 'border-border bg-canvas text-ink hover:bg-black/[0.03]'
                          }`}
                          onClick={() => {
                            setProfileId(profile.profileId);
                            setNodeKey('');
                          }}
                        >
                          {profile.displayName || profile.subjectLabel || profile.profileId}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-muted" htmlFor={`spine-search-${particleId}`}>
                      搜索主干节点
                    </label>
                    <input
                      id={`spine-search-${particleId}`}
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="节点名 / 模块 / ID"
                      className="mt-1.5 w-full rounded-lg border border-border bg-canvas px-3 py-2 text-sm text-ink outline-none focus:border-primary"
                    />
                  </div>

                  <div className="space-y-1">
                    {filteredNodes.length === 0 ? (
                      <p className="py-6 text-center text-sm text-muted">暂无匹配节点</p>
                    ) : (
                      filteredNodes.map((node) => {
                        const key = `${selectedProfile?.profileId}::${node.spineId}::${node.nodeId}`;
                        const selected = nodeKey === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`flex w-full flex-col gap-0.5 rounded-lg border px-3 py-2 text-left transition ${
                              selected
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:bg-black/[0.03]'
                            }`}
                            onClick={() => setNodeKey(key)}
                          >
                            <span className="text-sm font-medium text-ink">
                              {node.nodeTitle || node.nodeId}
                            </span>
                            <span className="text-[11px] text-muted">
                              {[node.moduleLabel || node.moduleKey, node.spineTitle]
                                .filter(Boolean)
                                .join(' · ') || node.spineId}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              ) : null}

              {error ? <p className="text-xs text-danger">{error}</p> : null}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
              {editTarget === 'primary' && primaryBound ? (
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm text-danger hover:bg-danger/5 disabled:opacity-50"
                  disabled={saving || loadingOptions}
                  onClick={() => { void handleClearPrimary(); }}
                >
                  清空绑定
                </button>
              ) : editTarget === 'secondary' && secondaryLabel ? (
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm text-danger hover:bg-danger/5 disabled:opacity-50"
                  disabled={saving || loadingOptions}
                  onClick={() => { void handleClearSecondary(); }}
                >
                  清除次级
                </button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm text-muted hover:bg-black/[0.04]"
                  disabled={saving}
                  onClick={() => setEditorOpen(false)}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                  disabled={saving || loadingOptions || !nodeKey}
                  onClick={() => { void handleSave(); }}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                  {editTarget === 'secondary' ? '保存次级' : '保存为手动绑定'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function bindingViewStatusBound(binding: Record<string, unknown> | undefined) {
  if (!binding) return false;
  return readText(binding, ['status']).toLowerCase() === 'bound'
    && Boolean(readText(binding, ['spineNodeId']))
    && Boolean(readText(binding, ['subjectValueProfileId']));
}
